import { DashboardContainerProps } from "@/components/Dashboard/UI/Cards/Interfaces/DashboardContainer";
import { EmptyContainer } from "@/components/Dashboard/UI/Containers/EmptyContainer";
import { BidSection } from "@/components/Dashboard/UI/Section/BidSection";
import { InvoiceSection } from "@/components/Dashboard/UI/Section/InvoiceSection";
import { MiniProjectListContainer } from "@/components/Dashboard/UI/Section/MiniProjectListContainer";
import { ProjectSection } from "@/components/Dashboard/UI/Section/ProjectSection";
import { TaskSection } from "@/components/Dashboard/UI/Section/TaskSection";
import { GridPartitionWrapper } from "@/components/Dashboard/Utils/GridPartitionWrapper";
import { useEffect, useState } from "react";

export const SECTION_REGISTRY = {
    project: {
        component: ProjectSection,
        getProps: (props: DashboardContainerProps) => ({
            sorted_projects: props.sorted_projects,
            isNewCard: props.isNewCard,
            markAsSeen: props.markAsSeen
        }),
        grid_columns: 1,
    },
    task: {
        component: TaskSection,
        getProps: (props: DashboardContainerProps) => ({
            sorted_tasks: props.sorted_tasks,
            sorted_assignments: props.sorted_assignments,
            isNewCard: props.isNewCard,
            markAsSeen: props.markAsSeen,
        }),
        grid_columns: 1,
    },
    bid: {
        component: BidSection,
        getProps: (props: DashboardContainerProps) => ({
            sorted_bids: props.sorted_bids,
            isNewCard: props.isNewCard,
            markAsSeen: props.markAsSeen,
        }),
        grid_columns: 1,
    },
    invoice: {
        component: InvoiceSection,
        getProps: (props: DashboardContainerProps) => ({
            sorted_invoices: props.sorted_invoices,
            isNewCard: props.isNewCard,
            markAsSeen: props.markAsSeen,
        }),
        grid_columns: 1,
    },
    mini_project_list: {
        component: MiniProjectListContainer,
        getProps: () => ({}), // No props needed; will get the project list from the component itself
        grid_columns: 2,
    }
} as const;

/**
 * `useDashboardSectionManager` is a custom hook that manages the sections displayed on the dashboard.
 * It determines which sections to render based on user preferences and provides utilities for
 * managing and rendering these sections.
 *
 * @param preferences - An array of strings representing the desired sections and their order.
 *                      These strings must correspond to keys in `SECTION_REGISTRY`.
 * @param initialSections - An array of strings representing the initial sections to be displayed.
 *                          These strings must correspond to keys in `SECTION_REGISTRY`.
 *
 * @returns An object containing:
 *  - `sections`: The current array of section names to be displayed.
 *  - `setSections`: A function to update the `sections` state.
 *  - `renderSections`: A function that takes `DashboardContainerProps` and returns the rendered
 *    React components for the sections. It filters out any invalid section names from `preferences`.
 *  - `getAvailableSections`: A function that returns an array of all possible section names
 *    defined in `SECTION_REGISTRY`.
 *  - `isValidSection`: A type guard function that checks if a given string is a valid section key.
 *
 * The `SECTION_REGISTRY` is a central configuration object where each key is a section name
 * and the value is an object containing:
 *  - `component`: The React component to render for the section.
 *  - `getProps`: A function that extracts the necessary props for the section component from the
 *    main `DashboardContainerProps`.
 *
 * To add a new section to the dashboard, you need to:
 * 1. Create the section component.
 * 2. Add a new entry to `SECTION_REGISTRY` with the section name, component, and `getProps` function.
 */
export function useDashboardSectionManager(preferences: string[], initialSections: string[] = []) {
    const [sections, setSections] = useState<string[]>(preferences)

    // This renders just the sections itself (it needs the wrapper from renderFullContainer to be displayed in a grid)
    const renderSections = (containerProps: DashboardContainerProps) => {
        // Used to stretch out sections if grid_columns is greater than 1
        const colSpanClasses: { [key: number]: string } = {
            1: "md:col-span-1",
            2: "md:col-span-2",
            3: "md:col-span-3",
            4: "md:col-span-4",
        }

        return sections
            .filter(section => section in SECTION_REGISTRY) // Only render valid sections
            .map((section) => {
                const sectionConfig = SECTION_REGISTRY[section as keyof typeof SECTION_REGISTRY]
                const SectionComponent = sectionConfig.component;
                const sectionProps = sectionConfig.getProps(containerProps)
                const spanClass = colSpanClasses[sectionConfig.grid_columns] ?? "md:col-span-1";

                return (
                    <div key={section} className={spanClass}>
                        <SectionComponent {...(sectionProps as any)} />
                    </div>
                )
            })
    }

    // This renders all the sections with the grid partition wrapper so that the sections are displayed in a dynamic grid
    const renderFullContainer = (containerProps: DashboardContainerProps) => {
        if (sections.length === 0) {
            return <EmptyContainer />
        }

        return (
            <GridPartitionWrapper grid_columns={getTotalGridColumns(sections)}>
                {renderSections(containerProps)}
            </GridPartitionWrapper>
        )
    }

    const getTotalGridColumns = (sections: string[]) => {
        // If the section is not in the registry, return 0
        if (!sections.every(section => section in SECTION_REGISTRY)) {
            return 0
        }

        return sections.map(section => SECTION_REGISTRY[section as keyof typeof SECTION_REGISTRY].grid_columns).reduce((acc, curr) => acc + curr, 0)
    }

    const getAvailableSections = () => {
        return Object.keys(SECTION_REGISTRY);
    }

    const isValidSection = (section: string): section is keyof typeof SECTION_REGISTRY => {
        return Object.keys(SECTION_REGISTRY).includes(section);
    }

    useEffect(() => {
        // Filter the initial sections to only include valid and user preferences
        setSections(initialSections.filter(section => isValidSection(section) && preferences.includes(section)));
    }, [initialSections, setSections, preferences]);

    return {
        sections,
        setSections,
        getAvailableSections,
        isValidSection,
        renderFullContainer,
    }
}